import { useMemo, useRef, useState, useEffect } from 'react';
import { FiDownload } from 'react-icons/fi';
import {
  calculateRoi,
  formatCurrency,
  SCENARIO_DESCRIPTIONS,
  SCENARIO_DESCRIPTIONS_ROI,
  ROLE_TYPES,
  SENIORITIES,
  setRoleTypes,
  setSeniorities,
  ROLE_TYPE_ALIASES,
  GROSS_MID,
  SMULT,
  SU_SEN,
  getBenchmarkZone,
  getRegionBenchmark,
  getRegionDisplayName,
  getRegionCurrency,
  REGION_CURRENCY,
} from '../utils/roiCalculator';
import { generateBlueprintData } from '../utils/blueprintGenerator';
import { exportBlueprintAsHtml, exportBlueprintAsPdfServer, exportBlueprintAsPdfClient } from '../utils/exportBlueprint';
import { saveProposal, saveCalculation, fetchMaster, fetchRates, saveFormData, fetchFormData } from '../services/api';
import RoleProfilesPanel from '../components/RoleProfilesPanel';

const defaultRoles = [
  { name: 'Tech / Team Lead', roleType: 'Architecture / Eng Mgmt', seniority: 'Lead', fte: 1 },
  { name: 'Backend Developer', roleType: 'Backend', seniority: 'Senior', fte: 2 },
  { name: 'Backend Developer', roleType: 'Backend', seniority: 'Mid', fte: 2 },
  { name: 'Frontend Developer', roleType: 'Frontend', seniority: 'Senior', fte: 1 },
  { name: 'Frontend Developer', roleType: 'Frontend', seniority: 'Mid', fte: 2 },
  { name: 'Database / Data Eng', roleType: 'Database', seniority: 'Senior', fte: 1 },
  { name: 'DevOps Engineer', roleType: 'DevOps / SRE', seniority: 'Senior', fte: 1 },
  { name: 'QA / Test Eng', roleType: 'QA / Test', seniority: 'Mid', fte: 2 },
  { name: 'Operations Eng', roleType: 'DevOps / SRE', seniority: 'Mid', fte: 1 },
  { name: 'Scrum Master', roleType: 'Scrum', seniority: 'Senior', fte: 1 },
  { name: 'Business Analyst', roleType: 'Business', seniority: 'Senior', fte: 1 },
];

/** Build monthly series for the two charts */
function buildChartSeries({ horizonMonths, rampPercent, localMonthly, springUpMonthly, setupFee }) {
  const months = [];
  const rampMonths = Math.max(1, Math.round((horizonMonths * (100 - rampPercent)) / 100 / 6) || 5);
  // simpler: use effective ramp ~ 3–9 mo scaled by team
  const effectiveRamp = Math.max(3, Math.min(9, Math.round(4 + (100 - rampPercent) / 20)));

  let cumulative = -setupFee; // start negative due to setup fee
  for (let m = 1; m <= horizonMonths; m++) {
    const rampFactor = Math.min(1, m / effectiveRamp);
    const local = localMonthly * rampFactor;
    const spring = springUpMonthly * rampFactor + (m === 1 ? setupFee : 0);
    const net = local - (springUpMonthly * rampFactor);
    cumulative += net;
    months.push({
      month: m,
      local,
      springUp: springUpMonthly * rampFactor,
      cumulative,
    });
  }
  return months;
}

function formatK(value) {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}00k`.replace('000k', '000k');
  if (Math.abs(value) >= 1000) return `${Math.round(value / 1000)}k`;
  return String(Math.round(value));
}

/** Simple SVG area/line chart */
function AreaChart({ data, dataKey, color, fillOpacity = 0.25, height = 220, yTicks }) {
  if (!data.length) return null;
  const w = 520;
  const h = height;
  const pad = { top: 16, right: 16, bottom: 28, left: 44 };
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;

  const values = data.map((d) => d[dataKey]);
  const minY = Math.min(0, ...values);
  const maxY = Math.max(...values, 1);
  const range = maxY - minY || 1;

  const x = (i) => pad.left + (i / (data.length - 1 || 1)) * innerW;
  const y = (v) => pad.top + innerH - ((v - minY) / range) * innerH;

  const linePath = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d[dataKey]).toFixed(1)}`)
    .join(' ');
  const areaPath =
    linePath +
    ` L ${x(data.length - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;

  const ticks = yTicks || [
    minY,
    minY + range * 0.25,
    minY + range * 0.5,
    minY + range * 0.75,
    maxY,
  ];

  // x labels: show every ~5 months
  const xStep = Math.max(1, Math.floor(data.length / 6));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="chart-svg" preserveAspectRatio="xMidYMid meet">
      {/* grid + y labels */}
      {ticks.map((t, i) => (
        <g key={i}>
          <line
            x1={pad.left}
            x2={w - pad.right}
            y1={y(t)}
            y2={y(t)}
            stroke="#e8ecf2"
            strokeWidth="1"
          />
          <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" className="chart-axis">
            {formatK(t)}
          </text>
        </g>
      ))}
      {/* area + line */}
      <path d={areaPath} fill={color} fillOpacity={fillOpacity} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
      {/* x labels */}
      {data.map((d, i) =>
        i % xStep === 0 || i === data.length - 1 ? (
          <text key={i} x={x(i)} y={h - 8} textAnchor="middle" className="chart-axis">
            {d.month}
          </text>
        ) : null
      )}
      <text x={w / 2} y={h - 2} textAnchor="middle" className="chart-axis-label">
        Month
      </text>
    </svg>
  );
}

/** Dual line chart for Local vs SpringUp monthly cost */
function DualLineChart({ data, height = 220 }) {
  if (!data.length) return null;
  const w = 520;
  const h = height;
  const pad = { top: 16, right: 16, bottom: 28, left: 44 };
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;

  const allVals = data.flatMap((d) => [d.local, d.springUp]);
  const minY = 0;
  const maxY = Math.max(...allVals, 1);
  const range = maxY - minY || 1;

  const x = (i) => pad.left + (i / (data.length - 1 || 1)) * innerW;
  const y = (v) => pad.top + innerH - ((v - minY) / range) * innerH;

  const pathFor = (key) =>
    data
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d[key]).toFixed(1)}`)
      .join(' ');

  const areaFor = (key, color) => {
    const line = pathFor(key);
    return (
      line +
      ` L ${x(data.length - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`
    );
  };

  const ticks = [0, maxY * 0.25, maxY * 0.5, maxY * 0.75, maxY];
  const xStep = Math.max(1, Math.floor(data.length / 6));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="chart-svg" preserveAspectRatio="xMidYMid meet">
      {ticks.map((t, i) => (
        <g key={i}>
          <line
            x1={pad.left}
            x2={w - pad.right}
            y1={y(t)}
            y2={y(t)}
            stroke="#e8ecf2"
            strokeWidth="1"
          />
          <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" className="chart-axis">
            {formatK(t)}
          </text>
        </g>
      ))}
      <path d={areaFor('local')} fill="#2e5aac" fillOpacity="0.08" />
      <path d={areaFor('springUp')} fill="#00a6a6" fillOpacity="0.12" />
      <path d={pathFor('local')} fill="none" stroke="#2e5aac" strokeWidth="2.5" strokeLinejoin="round" />
      <path d={pathFor('springUp')} fill="none" stroke="#00a6a6" strokeWidth="2.5" strokeLinejoin="round" />
      {data.map((d, i) =>
        i % xStep === 0 || i === data.length - 1 ? (
          <text key={i} x={x(i)} y={h - 8} textAnchor="middle" className="chart-axis">
            {d.month}
          </text>
        ) : null
      )}
      <text x={w / 2} y={h - 2} textAnchor="middle" className="chart-axis-label">
        Month
      </text>
    </svg>
  );
}

function HomePage() {
  const [scenario, setScenario] = useState('A');
  const [region, setRegion] = useState('DE');
  const [currency, setCurrency] = useState(getRegionCurrency('DE'));
  const [overhead, setOverhead] = useState(0);
  const [horizonMonths, setHorizonMonths] = useState(12);
  const [rampPercent, setRampPercent] = useState(0);
  const [setupFee, setSetupFee] = useState(0);
  const [roles, setRoles] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);
  const [masterRates, setMasterRates] = useState({ localRates: {}, springUpRates: {} });
  console.log('masterRates:---------------------', masterRates);
  const [implementationWeeks, setImplementationWeeks] = useState(0);      // for B
  const [handoverUplift, setHandoverUplift] = useState(0);               // for C
  const [legacyRampDownModel, setLegacyRampDownModel] = useState('A');    // for C
  const [legacyRampDownDuration, setLegacyRampDownDuration] = useState(0); // for C
  const [activeView, setActiveView] = useState('context');
  const [deliveryModel, setDeliveryModel] = useState('BOT / Nano GCC');
  const [productStage, setProductStage] = useState('Active development');
  const [supportModel, setSupportModel] = useState('Extended hours');
  useEffect(() => {
    setCurrency(getRegionCurrency(region));
  }, [region]);

  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');

  const [focusAreas, setFocusAreas] = useState([]);
  const [primaryObjectives, setPrimaryObjectives] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [techStack, setTechStack] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [validationMessage, setValidationMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [roleProfileCatalog, setRoleProfileCatalog] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const blueprintCardRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    async function loadMasterAndProfiles() {
      try {
        const master = await fetchMaster();
        const rates = await fetchRates();
        if (!mounted) return;
        setRoleTypes(master.roleTypes || []);
        if (master.seniorities) setSeniorities(master.seniorities);
        setMasterRates(rates || {});
      } catch (err) {
        console.warn('Unable to fetch master data', err);
      }
      try {
        const url = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/master/role-profiles';
        const resp = await fetch(url);
        const profileData = await resp.json();
        if (!mounted) return;
        setRoleProfileCatalog(profileData.roleProfiles || []);
      } catch (err) {
        console.warn('Unable to fetch role profile catalog', err);
      }
    }

    async function loadUserFormData() {
      try {
        const token = localStorage.getItem('jwt');
        if (!token) return;
        const res = await fetchFormData();
        const data = res?.formData;
        if (!data) return;
        // apply saved values selectively
        if (data.companyName) setCompanyName(data.companyName);
        if (data.industry) setIndustry(data.industry);
        if (data.scenario) setScenario(data.scenario);
        if (data.region) setRegion(data.region);
        if (data.currency) setCurrency(data.currency);
        if (data.deliveryModel) setDeliveryModel(data.deliveryModel);

        if (data.overhead !== undefined) setOverhead(Number(data.overhead));
        if (data.horizonMonths !== undefined) setHorizonMonths(Number(data.horizonMonths));
        if (data.rampPercent !== undefined) setRampPercent(Number(data.rampPercent));
        if (data.setupFee !== undefined) setSetupFee(Number(data.setupFee));
        if (data.implementationWeeks !== undefined) setImplementationWeeks(Number(data.implementationWeeks));
        if (data.handoverUplift !== undefined) setHandoverUplift(Number(data.handoverUplift));
        if (data.legacyRampDownModel) setLegacyRampDownModel(data.legacyRampDownModel);
        if (data.legacyRampDownDuration !== undefined) setLegacyRampDownDuration(Number(data.legacyRampDownDuration));

        if (Array.isArray(data.focusAreas)) setFocusAreas(data.focusAreas);
        if (Array.isArray(data.primaryObjectives)) setPrimaryObjectives(data.primaryObjectives);
        if (Array.isArray(data.compliance)) setCompliance(data.compliance);
        if (Array.isArray(data.techStack)) setTechStack(data.techStack);

        if (Array.isArray(data.roles) && data.roles.length) {
          // Keep the flags that were saved so manual edits survive a refresh
          const rolesClean = data.roles.map((r) => ({
            ...r,
            _userLocalEdited: !!r._userLocalEdited,
            _userSpringEdited: !!r._userSpringEdited,
          }));
          setRoles(rolesClean);
        }
      } catch (err) {
        // ignore fetch errors
      }
    }

    // initial load
    loadMasterAndProfiles();
    loadUserFormData();

    // listen for auth changes (login/logout) so we can reload user data without full page refresh
    const onAuthChanged = () => {
      // when logged in, fetch user's saved form and role profiles (master endpoint merges if auth present)
      const token = localStorage.getItem('jwt');
      if (token) {
        loadUserFormData();
        // fetch role profiles again with auth to include user profiles
        (async () => {
          try {
            const url = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/master/role-profiles';
            const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const profileData = await resp.json();
            if (!mounted) return;
            setRoleProfileCatalog(profileData.roleProfiles || []);
          } catch (err) {
            console.warn('Unable to fetch role profile catalog after auth change', err);
          }
        })();
      }
    };
    window.addEventListener('auth:changed', onAuthChanged);

    return () => { mounted = false; window.removeEventListener('auth:changed', onAuthChanged); };
  }, []);

  // auto-save form data when relevant fields change (debounced)
  const saveTimer = useRef(null);
  const gatherFormData = () => ({
    scenario,
    region,
    currency,
    deliveryModel,
    productStage,
    supportModel,
    companyName,
    industry,
    focusAreas,
    primaryObjectives,
    compliance,
    techStack,
    roles,
    // ROI settings that must survive refresh
    overhead,
    horizonMonths,
    rampPercent,
    setupFee,
    implementationWeeks,
    handoverUplift,
    legacyRampDownModel,
    legacyRampDownDuration,
  });

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (!token) return; // only save for authenticated users
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await saveFormData(gatherFormData());
      } catch (err) {
        // ignore save errors for now
      }
    }, 1000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [scenario, region, currency, deliveryModel, productStage, supportModel, companyName, industry, JSON.stringify(focusAreas), JSON.stringify(primaryObjectives), JSON.stringify(compliance), JSON.stringify(techStack), JSON.stringify(roles)]);

  // Recompute local + SpringUp rates when overhead / region / masterRates change.
  // STATIC rates only — no FX conversion.
  // master.json holds rates per region key:
  //   roles[].rates: IN | EU | USD | CHF
  //   springUpRates: EUR | USD | CHF  (with default + role-specific overrides)
  useEffect(() => {
    setRoles((prev) => {
      if (!prev || !prev.length) return prev;

      // Map selected region → rate-card keys
      // roles use "EU", springUpRates use "EUR"
      const targetCurrency =
        masterRates.currencyByRegion?.[region] || getRegionCurrency(region); // EUR | USD | CHF
      const rolesZone =
        targetCurrency === 'EUR' ? 'EU' :
          targetCurrency === 'USD' ? 'USD' :
            targetCurrency === 'CHF' ? 'CHF' : 'EU';
      const springCurrency =
        targetCurrency === 'EUR' ? 'EUR' :
          targetCurrency === 'USD' ? 'USD' :
            targetCurrency === 'CHF' ? 'CHF' : 'EUR';

      const rolesList = masterRates.roles || [];
      const springByCurrency = masterRates.springUpRates?.[springCurrency] || {};
      const normalize = (s = '') => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');

      return prev.map((r) => {
        const roleType = r.roleType || r.name || '';
        const senior = r.seniority || 'Mid';
        const normalized = ROLE_TYPE_ALIASES[roleType] || roleType;
        const target = normalize(roleType);

        // Find matching role in master
        let masterRole = rolesList.find((m) => m.name === roleType);
        if (!masterRole) {
          masterRole =
            rolesList.find((m) => normalize(m.name) === target) ||
            rolesList.find(
              (m) =>
                normalize(m.name).includes(target) ||
                target.includes(normalize(m.name))
            );
        }

        // ---------- LOCAL rate (Europe local cost for selected region) ----------
        let benchmark = null;

        if (
          masterRole &&
          masterRole.rates &&
          masterRole.rates[rolesZone] &&
          masterRole.rates[rolesZone][senior] !== undefined
        ) {
          // Master rate is treated as base → apply overhead on top
          const base = Number(masterRole.rates[rolesZone][senior]);
          benchmark = Math.round((base * (1 + overhead / 100)) / 50) * 50;
        } else {
          // Fallback: annual benchmark × seniority × overhead → monthly
          const annual = getRegionBenchmark(region)[normalized] || 0;
          if (!annual) {
            return r;
          }
          const sm = SMULT[senior] || 1;
          const monthly = (annual * sm * (1 + overhead / 100)) / 12;
          benchmark = Math.round(monthly / 50) * 50;
        }

        // ---------- SpringUp rate (static from springUpRates[EUR|USD|CHF]) ----------
        // Look up: role-specific override → normalized name → default
        const roleSpringTable =
          springByCurrency[roleType] ||
          springByCurrency[normalized] ||
          {};
        const defaultSpringTable = springByCurrency.default || {};

        const springRate =
          roleSpringTable[senior] ??
          defaultSpringTable[senior] ??
          r.springUpPerFte ??
          0;

        const fteNum = Number(r.fte) || 0;
        const localPerFte = r._userLocalEdited ? r.localPerFte : benchmark;
        const springUpPerFte = r._userSpringEdited ? r.springUpPerFte : Number(springRate);

        return {
          ...r,
          localPerFte,
          springUpPerFte,
          localMonthly: Number.isFinite(fteNum) ? fteNum * Number(localPerFte || 0) : '',
          springUpMonthly:
            Number.isFinite(fteNum) && Number.isFinite(Number(springUpPerFte))
              ? fteNum * Number(springUpPerFte)
              : '',
          saveMonthly:
            (Number(fteNum * Number(localPerFte || 0)) || 0) -
            (Number(fteNum * Number(springUpPerFte || 0)) || 0),
        };
      });
    });
  }, [overhead, region, masterRates, currency]);
  // Force currency conversion when user navigates back to the ROI step
  useEffect(() => {
    if (activeView !== 'roi') return;

    // trigger the same conversion logic by slightly updating a dependency
    // (the main useEffect will pick it up because currency is now in the deps)
    setRoles((prev) => [...prev]);   // shallow copy forces the main effect to re-evaluate
  }, [activeView]);

  const steps = [
    { id: 'context', n: '1', title: 'Context', subtitle: 'Company & scenario' },
    { id: 'scope', n: '2', title: 'Scope', subtitle: 'Functions & tech' },
    { id: 'roi', n: '3', title: 'ROI', subtitle: 'Financial case' },
    { id: 'blueprint', n: '4', title: 'Blueprint', subtitle: 'AI implementation plan' },
  ];
  const activeStepIndex = steps.findIndex((step) => step.id === activeView);

  const validateStep = (stepId) => {
    const errors = {};

    if (stepId === 'context') {
      if (!companyName.trim()) errors.companyName = 'Company is required.';
      if (!industry.trim()) errors.industry = 'Industry / domain is required.';
      if (!deliveryModel.trim()) errors.deliveryModel = 'Delivery model is required.';
      if (!scenario) errors.scenario = 'Scenario is required.';
      if (!primaryObjectives.length || primaryObjectives.every((value) => !value.trim())) {
        errors.primaryObjectives = 'Select at least one primary objective.';
      }
    }

    if (stepId === 'scope') {
      if (!focusAreas.length || focusAreas.every((value) => !value.trim())) {
        errors.focusAreas = 'Select at least one function.';
      }
      if (!techStack.length || techStack.every((value) => !value.trim())) {
        errors.techStack = 'Select at least one technology.';
      }
      if (!primaryObjectives.length || primaryObjectives.every((value) => !value.trim())) {
        errors.primaryObjectives = 'Select at least one primary objective.';
      }
      if (!compliance.length || compliance.every((value) => !value.trim())) {
        errors.compliance = 'Select at least one Compliance / regulatory context.';
      }
    }

    if (stepId === 'roi') {
      if (!roles.length) {
        errors.roles = 'Add at least one role.';
      } else {
        roles.forEach((role, index) => {
          if (!role.name?.trim()) errors[`roleName_${index}`] = 'Role name is required.';
          if (!role.roleType?.trim()) errors[`roleType_${index}`] = 'Role type is required.';
          if (!role.seniority?.trim()) errors[`roleSeniority_${index}`] = 'Seniority is required.';
          if (!(Number(role.fte) > 0)) errors[`roleFte_${index}`] = 'FTE must be greater than 0.';
        });
      }
    }

    return errors;
  };

  const setStepErrors = (errors) => {
    const hasError = Object.keys(errors).length > 0;
    setValidationErrors(errors);
    setValidationMessage(hasError ? 'Please complete required fields before continuing.' : '');
    return !hasError;
  };

  const handleStepClick = (stepId) => {
    const nextStepIndex = steps.findIndex((step) => step.id === stepId);
    if (nextStepIndex <= activeStepIndex) {
      setActiveView(stepId);
      setValidationErrors({});
      setValidationMessage('');
      return;
    }

    const currentErrors = validateStep(activeView);
    if (setStepErrors(currentErrors)) {
      setActiveView(stepId);
    }
  };

  const handleContinue = () => {
    const currentErrors = validateStep(activeView);
    if (!setStepErrors(currentErrors)) {
      return;
    }

    const nextStep = steps[activeStepIndex + 1];
    if (nextStep) {
      // If moving to the blueprint step, persist the ROI Financial Case for the user
      if (nextStep.id === 'blueprint') {
        const token = localStorage.getItem('jwt');
        if (token) {
          // fire-and-forget save of calculation
          (async () => {
            console.log('Saving ROI calculation on Generate Blueprint...');
            try {
              await saveCalculation({
                scenario,
                region,
                overhead,
                setupFee,
                horizonMonths,
                rampPercent,
                implementationWeeks,
                handoverUplift,
                legacyRampDownModel,
                legacyRampDownDuration,
                companyName,
                industry,
                deliveryModel,
                productStage,
                supportModel,
                focusAreas,
                techStack,
                primaryObjectives,
                compliance,
                roles,
                summary,
              });
            } catch (err) {
              console.warn('Failed to save ROI calculation on Generate Blueprint', err);
            }
          })();
        }
      }
      setActiveView(nextStep.id);
    }
  };

  const validateAllSteps = () => {
    const allErrors = steps.reduce((acc, step) => ({ ...acc, ...validateStep(step.id) }), {});
    return setStepErrors(allErrors);
  };

  const handleSubmitProposal = async () => {
    if (!validateAllSteps()) {
      setSaveStatus('error');
      setSaveMessage('Please fix the highlighted fields before submitting.');
      return;
    }

    setSaveStatus('saving');
    setSaveMessage('Saving proposal...');

    try {
      await saveProposal({
        scenario,
        region,
        overhead,
        setupFee,
        horizonMonths,
        rampPercent,
        implementationWeeks,
        handoverUplift,
        legacyRampDownModel,
        legacyRampDownDuration,
        companyName,
        industry,
        deliveryModel,
        productStage,
        supportModel,
        focusAreas,
        techStack,
        primaryObjectives,
        compliance,
        roles,
        summary,
      });
      setSaveStatus('success');
      setSaveMessage('Proposal saved successfully.');
    } catch (error) {
      setSaveStatus('error');
      setSaveMessage(error.message || 'Unable to save proposal.');
    }
  };

  const { summary, roleCosts } = useMemo(
    () =>
      calculateRoi({
        region,
        overhead,
        roles,
        scenario,
        horizonMonths,
        rampPercent,
        setupFee,
        implementationWeeks,
        handoverUplift,
        legacyRampDownModel,
        legacyRampDownDuration,
        currencyOverride: currency,
        // conversionRates removed — static rates per region in master.json
      }),
    [region, overhead, roles, scenario, horizonMonths, rampPercent, setupFee, implementationWeeks, handoverUplift, legacyRampDownModel, legacyRampDownDuration, currency, masterRates]
  );

  const matchedBlueprintProfiles = useMemo(() => {
    if (!roles.length || !roleProfileCatalog.length) return [];

    const normalize = (s = '') => (s || '').toLowerCase().trim();
    const matches = (profile, key) => {
      const name = normalize(profile.name);
      const summaryText = normalize(profile.summary);
      return name.includes(key) || key.includes(name) || summaryText.includes(key);
    };

    const ordered = [];
    const added = new Set();

    // iterate roles in order and collect matching profiles
    for (const role of roles) {
      const keys = [];
      if (role.name) keys.push(normalize(role.name));
      if (role.roleType) keys.push(normalize(role.roleType));

      for (const key of keys) {
        if (!key) continue;
        for (const profile of roleProfileCatalog) {
          if (added.has(profile.id)) continue;
          if (matches(profile, key)) {
            ordered.push(profile);
            added.add(profile.id);
          }
        }
      }
    }

    // append any remaining matching profiles
    for (const profile of roleProfileCatalog) {
      if (added.has(profile.id)) continue;
      for (const role of roles) {
        const key1 = normalize(role.name);
        const key2 = normalize(role.roleType);
        if ((key1 && matches(profile, key1)) || (key2 && matches(profile, key2))) {
          ordered.push(profile);
          added.add(profile.id);
          break;
        }
      }
    }

    return ordered;
  }, [roles, roleProfileCatalog]);

  const chartData = useMemo(
    () =>
      buildChartSeries({
        horizonMonths,
        rampPercent,
        localMonthly: summary.localTotal ?? 125650,
        springUpMonthly: summary.springUpTotal ?? 51000,
        setupFee,
      }),
    [horizonMonths, rampPercent, summary.localTotal, summary.springUpTotal, setupFee]
  );

  const blueprintData = useMemo(
    () =>
      generateBlueprintData({
        scenario,
        roles,
        summary,
        region,
        setupFee,
        horizonMonths,
        rampPercent,
        implementationWeeks,
        handoverUplift,
        legacyRampDownModel,
        legacyRampDownDuration,
        companyName,
        focusAreas,
        techStack,
        deliveryModel,
        industry,
      }),
    [scenario, roles, summary, region, setupFee, horizonMonths, rampPercent, implementationWeeks, handoverUplift, legacyRampDownModel, legacyRampDownDuration]
  );

  const governanceItems = blueprintData.governanceText
    ? blueprintData.governanceText.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  const handleExport = async () => {
    if (!blueprintCardRef.current || pdfLoading) return;

    setPdfLoading(true);

    // Give React time to paint the loader before heavy work starts
    await new Promise((resolve) => setTimeout(resolve, 80));

    try {
      // Optional: save proposal first
      const token = localStorage.getItem('jwt');
      if (token) {
        try {
          await saveProposal({
            scenario,
            region,
            overhead,
            setupFee,
            horizonMonths,
            rampPercent,
            implementationWeeks,
            handoverUplift,
            legacyRampDownModel,
            legacyRampDownDuration,
            companyName,
            industry,
            deliveryModel,
            productStage,
            supportModel,
            focusAreas,
            techStack,
            primaryObjectives,
            compliance,
            roles,
            summary,
          });
        } catch (err) {
          console.warn('Failed to save blueprint before download', err);
        }
      }

      const currencyLabel =
        currency === 'EUR' ? 'EUR' :
          currency === 'USD' ? 'USD' :
            currency === 'CHF' ? 'CHF' : currency;

      const fileName = `SpringUp_Blueprint_${currencyLabel}.pdf`;

      try {
        await exportBlueprintAsPdfClient(blueprintCardRef.current, fileName);
      } catch (err) {
        console.error('Client PDF export failed', err);
        // fallback to server
        await exportBlueprintAsPdfServer(blueprintCardRef.current, fileName);
      }
    } catch (err) {
      console.error('PDF export failed', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const updateRole = (index, field, value) => {
    setRoles(prev => {
      const updated = [...prev];
      const prevEntry = { ...(updated[index] || {}) };

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      // Mark manual edits
      if (field === 'localPerFte') updated[index]._userLocalEdited = true;
      if (field === 'springUpPerFte') updated[index]._userSpringEdited = true;

      // Name sync + default seniority/fte when roleType changes
      if (field === 'roleType') {
        const prevRoleType = prevEntry.roleType || '';
        const prevName = prevEntry.name || '';

        if (!prevName || prevName === prevRoleType || prevName === 'Management') {
          updated[index].name = value;
        }

        // Special defaults for certain role types
        const roleTypeDefaults = {
          Management: { seniority: 'CTO', fte: 1 },
          'Technical Architect': { seniority: 'Solution Architect', fte: 1 },
          '24x7 Support': { seniority: 'DevOps/Cloud Support', fte: 5.2 },
          'Support / Service Desk': { seniority: 'Mid', fte: 1 },
        };

        // Normal roles must reset to a valid seniority (Mid / Senior / Lead)
        const defaults = roleTypeDefaults[value] || { seniority: 'Mid', fte: 1 };

        updated[index].seniority = defaults.seniority;
        updated[index].fte = defaults.fte;
      }

      const role = updated[index];

      if (['roleType', 'seniority'].includes(field)) {
        // STATIC rates from master.json — no FX conversion
        const roleName = role.roleType || role.name || '';
        const senior = role.seniority || 'Mid';
        const rolesList = masterRates.roles || [];

        const targetCurrency =
          masterRates.currencyByRegion?.[region] || getRegionCurrency(region); // EUR | USD | CHF
        const rolesZone =
          targetCurrency === 'EUR' ? 'EU' :
            targetCurrency === 'USD' ? 'USD' :
              targetCurrency === 'CHF' ? 'CHF' : 'EU';
        const springCurrency =
          targetCurrency === 'EUR' ? 'EUR' :
            targetCurrency === 'USD' ? 'USD' :
              targetCurrency === 'CHF' ? 'CHF' : 'EUR';

        const normalize = (s = '') => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
        const target = normalize(roleName);

        let masterRole = rolesList.find((r) => r.name === roleName);
        if (!masterRole) {
          masterRole = rolesList.find((r) => normalize(r.name) === target)
            || rolesList.find((r) => normalize(r.name).includes(target) || target.includes(normalize(r.name)));
        }

        // Local rate: static Europe rate for selected region currency
        let localCandidate = masterRole?.rates?.[rolesZone]?.[senior];
        if (localCandidate !== undefined && localCandidate !== null) {
          role.localPerFte = Number(localCandidate);
          role._userLocalEdited = false;
        } else {
          // fallback: GROSS_MID + SMULT + overhead
          const normalized = ROLE_TYPE_ALIASES[roleName] || roleName;
          const annual = getRegionBenchmark(region)[normalized] || 0;
          if (annual) {
            const sm = SMULT[senior] || 1;
            const benchmark = Math.round((annual * sm * (1 + overhead / 100)) / 12 / 50) * 50;
            role.localPerFte = benchmark;
            role._userLocalEdited = false;
          }
        }

        // SpringUp: springUpRates[EUR|USD|CHF][role|default][seniority]
        const springByCurrency = masterRates.springUpRates?.[springCurrency] || {};
        const roleSpring =
          springByCurrency[roleName]?.[senior] ??
          springByCurrency[ROLE_TYPE_ALIASES[roleName] || roleName]?.[senior] ??
          springByCurrency.default?.[senior];

        if (roleSpring !== undefined && roleSpring !== null) {
          role.springUpPerFte = Number(roleSpring);
          role._userSpringEdited = false;
        } else if (SU_SEN && typeof SU_SEN === 'object') {
          // SU_SEN is now { EUR: {...}, USD: {...}, CHF: {...} }
          const table = SU_SEN[springCurrency] || SU_SEN.EUR || {};
          if (table[senior] !== undefined) {
            role.springUpPerFte = table[senior];
            role._userSpringEdited = false;
          }
        }

        if (field === 'roleType' && value === '24x7 Support') {
          role.fte = 5.2;
        }
      }

      if ((field === 'fte' && value === '') || (field === 'localPerFte' && value === '') || (field === 'springUpPerFte' && value === '')) {
        role.localMonthly = '';
        role.springUpMonthly = '';
        role.saveMonthly = 0;
      }

      const fteNum = Number(role.fte);
      const localPerFteNum = Number(role.localPerFte);
      const springPerFteNum = Number(role.springUpPerFte);

      role.localMonthly = Number.isFinite(fteNum) && Number.isFinite(localPerFteNum) ? fteNum * localPerFteNum : '';
      role.springUpMonthly = Number.isFinite(fteNum) && Number.isFinite(springPerFteNum) ? fteNum * springPerFteNum : '';
      role.saveMonthly = (Number(role.localMonthly) || 0) - (Number(role.springUpMonthly) || 0);

      return updated;
    });

    // ========== CLEAR VALIDATION ERRORS WHEN USER FIXES THE FIELD ==========
    setValidationErrors((prev) => {
      const next = { ...prev };

      if (field === 'name') delete next[`roleName_${index}`];
      if (field === 'roleType') delete next[`roleType_${index}`];
      if (field === 'seniority') delete next[`roleSeniority_${index}`];
      if (field === 'fte') delete next[`roleFte_${index}`];

      // If no more role-related errors left, also clear the general "roles" error
      const stillHasRoleError = Object.keys(next).some((k) =>
        k.startsWith('roleName_') ||
        k.startsWith('roleType_') ||
        k.startsWith('roleSeniority_') ||
        k.startsWith('roleFte_')
      );
      if (!stillHasRoleError) {
        delete next.roles;
      }

      return next;
    });
  };

  const removeRole = (index) => {
    setRoles((prev) => prev.filter((_, i) => i !== index));
  };


  const addRole = () => {
    // Start with an empty role-type so the user must choose explicitly instead of defaulting
    const defaultRoleType = '';
    const defaultSeniority = 'Mid';
    // STATIC rates — no FX conversion
    const targetCurrency =
      masterRates.currencyByRegion?.[region] || getRegionCurrency(region); // EUR | USD | CHF
    const rolesZone =
      targetCurrency === 'EUR' ? 'EU' :
        targetCurrency === 'USD' ? 'USD' :
          targetCurrency === 'CHF' ? 'CHF' : 'EU';
    const springCurrency =
      targetCurrency === 'EUR' ? 'EUR' :
        targetCurrency === 'USD' ? 'USD' :
          targetCurrency === 'CHF' ? 'CHF' : 'EUR';

    const masterRole = defaultRoleType
      ? (masterRates.roles || []).find((r) => r.name === defaultRoleType)
      : null;

    // Local: static rate for selected region currency
    let preLocal = masterRole?.rates?.[rolesZone]?.[defaultSeniority] ?? '';
    if (preLocal !== '' && preLocal !== null && preLocal !== undefined) {
      preLocal = Number(preLocal);
    }

    // SpringUp: springUpRates[EUR|USD|CHF].default[seniority]
    const springByCurrency = masterRates.springUpRates?.[springCurrency] || {};
    const preSpring = defaultRoleType
      ? (springByCurrency.default?.[defaultSeniority] ?? '')
      : '';
    const fteVal = 1;
    const localMonthly = Number.isFinite(Number(preLocal)) ? Number(preLocal) * fteVal : '';
    const springMonthly = Number.isFinite(Number(preSpring)) ? Number(preSpring) * fteVal : '';

    setRoles((prev) => [
      ...prev,
      {
        name: '',
        roleType: defaultRoleType,
        seniority: defaultSeniority,
        fte: fteVal,
        localPerFte: preLocal,
        springUpPerFte: preSpring,
        localMonthly,
        springUpMonthly: springMonthly,
        saveMonthly: (Number(localMonthly) || 0) - (Number(springMonthly) || 0),
        _userLocalEdited: preLocal !== '' && preLocal !== null && preLocal !== undefined,
        _userSpringEdited: preSpring !== '' && preSpring !== null && preSpring !== undefined,
      },
    ]);
  };

  return (
    <div className="page-shell">
      <div className="stepper" id="stepper">
        {steps.map((step, index) => {
          const isCompleted = index < activeStepIndex;

          return (
            <button
              key={step.id}
              type="button"
              className={`stepper-btn ${activeView === step.id ? 'on' : ''} ${isCompleted ? 'completed' : ''}`}
              onClick={() => handleStepClick(step.id)}
            >
              <span className="n">
                {isCompleted ? '✓' : step.n}
              </span>
              <b>{step.title}</b>
              <span>{step.subtitle}</span>
            </button>
          );
        })}
      </div>

      {activeView === 'context' ? (
        <div className="card form-card">
          <h2>1 · Engagement context</h2>
          <p className="subhead">Tell us who this is for and what kind of engagement it is.</p>
          {validationMessage && activeView === 'context' && (
            <div className="error-banner">{validationMessage}</div>
          )}

          <div className="form-grid two-col">
            <div className={`form-group ${validationErrors.companyName ? 'field-error' : ''}`}>
              <label>Company</label>
              <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
              {validationErrors.companyName && <div className="field-error-message">{validationErrors.companyName}</div>}
            </div>
            <div className={`form-group ${validationErrors.industry ? 'field-error' : ''}`}>
              <label>Industry / domain</label>
              <input value={industry} onChange={(e) => setIndustry(e.target.value)} />
              {validationErrors.industry && <div className="field-error-message">{validationErrors.industry}</div>}
            </div>
            <div className={`form-group ${validationErrors.deliveryModel ? 'field-error' : ''}`}>
              <label>HQ region</label>
              <select value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="DE">Germany (DE)</option>
                <option value="CH">Switzerland (CH)</option>
                <option value="US">United States (US)</option>
              </select>
            </div>

            {/* <div className="form-group">
              <label>Currency</label>
              <div>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  disabled
                  style={{
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: 'none',
                    cursor: 'default',
                    paddingRight: '12px',   // remove extra space that was for the arrow
                  }}
                >
                  <option value={currency}>
                    {REGION_CURRENCY[region] || currency}
                  </option>
                </select>
              </div>
              <p className="hint">Default currency is selected from HQ region.</p>
            </div> */}

            <div className="form-group">
              <label> </label>
              <label> </label>
              <label> </label>
              <div style={{ paddingTop: '6px', fontSize: '15px', color: '#1f2937' }}>
                <label>Currency:- </label>   {REGION_CURRENCY[region] || currency}
              </div>
              {/* <span className="hint">Default currency is selected from HQ region.</span> */}
            </div>

          </div>

          <div className="form-row-two-columns">
            {/* Primary Objectives */}
            <div className={`form-group ${validationErrors.primaryObjectives ? 'field-error' : ''}`}>
              <label>Primary objectives</label>

              <div className="chip-row">
                {['Cost', 'Scale', 'Speed-to-market', 'Innovation', 'Exit / valuation'].map(
                  (option) => (
                    <button
                      key={option}
                      type="button"
                      className={`chip ${primaryObjectives.includes(option) ? 'active' : ''}`}
                      onClick={() => {
                        setPrimaryObjectives((prev) =>
                          prev.includes(option)
                            ? prev.filter((x) => x !== option)
                            : [...prev, option]
                        );
                      }}
                    >
                      {option}
                    </button>
                  )
                )}
              </div>

              {validationErrors.primaryObjectives && (
                <div className="field-error-message">
                  {validationErrors.primaryObjectives}
                </div>
              )}
            </div>

            {/* Delivery Model */}
            <div className={`form-group ${validationErrors.deliveryModel ? 'field-error' : ''}`}>
              <label>Delivery model</label>

              <select
                value={deliveryModel}
                onChange={(e) => setDeliveryModel(e.target.value)}
              >
                <option value="BOT / Nano GCC">BOT / Nano GCC</option>
                <option value="Managed offshore office">Managed offshore office</option>
                <option value="Build-Operate-Transfer">Build-Operate-Transfer</option>
                <option value="Staff augmentation">Staff augmentation</option>
              </select>

              {validationErrors.deliveryModel && (
                <div className="field-error-message">
                  {validationErrors.deliveryModel}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Scenario</label>
            <div className="scenario-grid">
              {Object.entries(SCENARIO_DESCRIPTIONS).map(([key, desc]) => (
                <button
                  key={key}
                  type="button"
                  className={`scenario-card ${scenario === key ? 'selected' : ''}`}
                  onClick={() => setScenario(key)}
                >
                  <div className="scenario-head">{key === 'A' ? 'A · New department' : key === 'B' ? 'B · Add-on to existing dept' : 'C · Replace existing dept'}</div>
                  <div className="scenario-text">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="nav-row">
            <div />
            <button type="button" className="btn primary" onClick={handleContinue}>
              Continue →
            </button>
          </div>
        </div>
      ) : activeView === 'scope' ? (
        <div className="card form-card">
          <h2>2 · Scope of work & technology</h2>
          <p className="subhead">Which functions, stack and constraints define the engagement.</p>
          {validationMessage && activeView === 'scope' && (
            <div className="error-banner">{validationMessage}</div>
          )}

          <div className={`form-group ${validationErrors.focusAreas ? 'field-error' : ''}`}>
            <label>Functions</label>
            <div className="chip-row">
              {['R&D / Product', 'QA', 'DevOps & Support', 'Data & AI', 'Cloud & Security', 'Operations'].map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`chip ${focusAreas.includes(item) ? 'active' : ''}`}
                  onClick={() => {
                    setFocusAreas((prev) =>
                      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item].slice(0, 6)
                    );
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
            {validationErrors.focusAreas && <div className="field-error-message">{validationErrors.focusAreas}</div>}
          </div>

          <div className={`form-group ${validationErrors.techStack ? 'field-error' : ''}`}>
            <label>Technology stack</label>
            <div className="chip-row">
              {['React', 'Angular', 'Vue', 'Spring Boot', 'Node.js', '.NET', 'Python', 'PostgreSQL', 'MongoDB', 'AWS', 'Azure', 'GCP', 'Kubernetes'].map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`chip ${techStack.includes(item) ? 'active' : ''}`}
                  onClick={() => {
                    setTechStack((prev) =>
                      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item].slice(0, 12)
                    );
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
            {validationErrors.techStack && <div className="field-error-message">{validationErrors.techStack}</div>}
          </div>

          <div className="form-grid two-col">
            <div className="form-group">
              <label>Product stage</label>
              <select
                value={productStage}
                onChange={(e) => setProductStage(e.target.value)}
              >
                <option value="Greenfield">Greenfield</option>
                <option value="Active development">Active development</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Modernization">Modernization</option>
              </select>
            </div>

            <div className="form-group">
              <label>Support model</label>
              <select
                value={supportModel}
                onChange={(e) => setSupportModel(e.target.value)}
              >
                <option value="Business hours">Business hours</option>
                <option value="Extended hours">Extended hours</option>
                <option value="24×7 follow-the-sun">24×7 follow-the-sun</option>
              </select>
            </div>
          </div>

          <div className={`form-group ${validationErrors.compliance ? 'field-error' : ''}`}>
            <label>Compliance / regulatory context</label>

            <div className="chip-row">
              {['GDPR', 'ISO 27001', 'HIPAA', 'FHIR', 'SOC 2', 'PCI-DSS'].map(
                (item) => (
                  <button
                    key={item}
                    type="button"
                    className={`chip ${compliance.includes(item) ? 'active' : ''}`}
                    onClick={() => {
                      setCompliance((prev) =>
                        prev.includes(item)
                          ? prev.filter((x) => x !== item)
                          : [...prev, item]
                      );
                    }}
                  >
                    {item}
                  </button>
                )
              )}
            </div>
            {validationErrors.compliance && (
              <div className="field-error-message">{validationErrors.compliance}</div>
            )}
          </div>
          <p className="subhead">These drive the reasoning, guardrails and non-functional sections of the AI blueprint (e.g. FHIR+HIPAA for health, GDPR for DACH).</p>

          <div className="nav-row">
            <button type="button" className="btn ghost" onClick={() => handleStepClick('context')}>
              ← Back
            </button>
            <button type="button" className="btn primary" onClick={handleContinue}>
              Continue →
            </button>
          </div>
        </div>
      ) : activeView === 'blueprint' ? (
        <div className="blueprint-view">
          <div className="card blueprint-card" ref={blueprintCardRef}>
            <h2>4 · AI implementation blueprint</h2>
            <div className="blueprint-sub">
              {blueprintData.companyName} · {blueprintData.scenarioLabel} · generated from intake + ROI, grounded in SpringUp method
            </div>
            <div className="badge-row">
              {blueprintData.focusAreas.map((item) => (
                <span className="badge" key={item}>{item}</span>
              ))}
              {blueprintData.techStack.map((item) => (
                <span className="badge" key={item}>{item}</span>
              ))}
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>1 · Engagement overview</span>
                <span className="tag">BOT / Nano GCC</span>
              </div>
              <div className="bp-sec-body">
                SpringUp Labs will establish a dedicated, self-managed team of The proposed engagement is sized at <b>{Number(blueprintData.teamSize ?? 0).toFixed(2)} FTE</b> in Pune under a Build-Operate-Transfer model, operating as a cross-continent extension of Helvetia Logistics SaaS. The team owns delivery end-to-end under a  <b>"You Build It, You Run It"</b> model, with a senior core installed first to build and govern it. Objective: scale, speed-to-market — while building transferable capability that adds to valuation on exit.
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>2 · Team composition</span>
                <span className="tag">Role catalogue</span>
              </div>
              <div className="bp-sec-body">
                <ul>
                  {blueprintData.roleLines.map((role) => {
                    const [roleName, ...rest] = role.title.split('—');

                    return (
                      <li key={role.title}>
                        <b>{roleName.trim()}</b>
                        {rest.length > 0 && ` — ${rest.join('—')}`}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>3 · ROLE PROFILES</span>
                <span className="tag">Selected roles</span>
              </div>
              <div className="bp-sec-body">
                <div className="blueprint-role-profiles">
                  {matchedBlueprintProfiles.length ? matchedBlueprintProfiles.map((profile, index) => (
                    <details key={profile.id || profile.name} className="blueprint-role-profile">
                      <summary>
                        <span className="bp-role-name">{profile.name}</span>
                        <span className="bp-role-arrow" aria-hidden="true">›</span>
                      </summary>
                      <div className="bp-role-body">
                        <p className="bp-role-summary">{profile.summary}</p>
                        {profile.seniority && (
                          <div className="bp-role-meta"><strong>Seniority:</strong> {profile.seniority}</div>
                        )}
                        {profile.suggestedFte && (
                          <div className="bp-role-meta"><strong>Suggested FTE:</strong> {profile.suggestedFte}</div>
                        )}
                        {profile.responsibilities?.length > 0 && (
                          <div className="bp-role-section">
                            <strong>Responsibilities</strong>
                            <ul>
                              {profile.responsibilities.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {profile.skills?.length > 0 && (
                          <div className="bp-role-section">
                            <strong>Skills</strong>
                            <ul>
                              {profile.skills.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </details>
                  )) : blueprintData.roleLines.map((role, index) => (
                    <details key={role.title} className="blueprint-role-profile">
                      <summary>
                        <span className="bp-role-name">{role.title}</span>
                        <span className="bp-role-arrow" aria-hidden="true">›</span>
                      </summary>
                      <div className="bp-role-body">
                        <p className="bp-role-summary">Selected team role for this capability.</p>
                        <div className="bp-role-meta"><strong>Role:</strong> {role.title}</div>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>4 · Implementation approach</span>
                <span className="tag">BOT phases</span>
              </div>
              <div className="bp-sec-body">
                Plan → Build → Operate → Transfer. The department is built around an experienced senior core (CTO R&amp;D India), integrated with your engineering team and governed through SpringUp's delivery-excellence model.
                <div className="why">Why: a new team needs an experienced lead who holds the knowledge and builds &amp; manages the team around them — de-risking stand-up and protecting quality.</div>
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>5 · Phased plan &amp; quality gates</span>
                <span className="tag">Transition method</span>
              </div>
              <div className="bp-sec-body">
                <ul>
                  <li><b>Search &amp; selection</b> — ~5 wk <span className="muted">· gate: client confirms senior core</span></li>
                  <li><b>Due diligence &amp; KT</b> — ~4 wk <span className="muted">· gate: KT completeness review</span></li>
                  <li><b>Reverse KT</b> — ~2 wk <span className="muted">· gate: reverse-presentation evaluation</span></li>
                  <li><b>Pilot &amp; shadowing</b> — ~3 wk <span className="muted">· gate: onboarding sign-off</span></li>
                  <li><b>Ramp to full op.</b> — ~6 wk <span className="muted">· gate: steady-state readiness</span></li>
                </ul>
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>6 · Timeline</span>
                <span className="tag">Reconciled with ROI</span>
              </div>
              <div className="bp-sec-body">
                <div className="timeline">
                  {blueprintData.timelineBars.map((item) => (
                    <div className="timeline-phase" key={item.label}>
                      <div className="timeline-label">{item.label}</div>
                      <div className="timeline-bar" style={{ width: item.width, background: item.color }}>{item.weeks ? `${item.weeks} wk` : 'ongoing'}</div>
                    </div>
                  ))}
                </div>
                <div className="hint">Total to full operation ≈ {blueprintData.totalDuration} — aligned with the ROI engine's ramp assumptions.</div>
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>7 · Why SpringUp implements it this way</span>
                <span className="tag">Reasoning</span>
              </div>
              <div className="bp-sec-body">
                <ul>
                  <li><b>Senior core first</b> — experienced leads hold knowledge and build the team around them.</li>
                  <li><b>Reverse KT as a gate</b> — proves comprehension before the team owns delivery.</li>
                  <li><b>Pune delivery</b> — top-5% talent, 3.5–4.5h EU overlap, cost-effective.</li>
                  <li><b>Cost-plus BOT</b> — ~57% cost advantage with a clean exit that adds to valuation.</li>
                </ul>
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>8 · Governance &amp; KPIs</span>
                <span className="tag">Delivery excellence</span>
              </div>
              <div className="bp-sec-body">
                {governanceItems.length > 1 ? (
                  <ul>
                    {governanceItems.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  blueprintData.governanceText || '(Bi)weekly management status meetings; sprint-end delivery, performance & quality audits. KPIs: ~13 Fibonacci SP/week/developer, negligible sprint spillover, near-zero blocking bugs, SLA adherence, developer-owned CI/CD. Knowledge managed centrally in Confluence/Jira with recorded sharing sessions.'
                )}
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>9 · Investment, cost &amp; ROI</span>
                <span className="tag">Business case</span>
              </div>
              <div className="bp-sec-body">
                <ul>
                  <li><b>Run-cost comparison</b> — local {getRegionDisplayName(blueprintData.region)} team (fully-loaded, incl. ~{overhead}% employer overhead) <b>{formatCurrency(blueprintData.summary.monthlyLocal, blueprintData.region || region, currency)} /mo</b> vs SpringUp <b>{formatCurrency(
                    summary.springUpBase ?? roleCosts.reduce((s, r) => s + (r.springUpMonthly || 0), 0),
                    region,
                    currency
                  )} /mo</b> for equivalent capacity.</li>
                  <li><b>Investment</b> — one-time setup fee <b>{formatCurrency(setupFee, region, currency)}</b>.the ramp period is the main investment window.</li>
                  <li><b>Return</b> — net savings  over {blueprintData.horizonMonths} months.<b>{formatCurrency(blueprintData.summary.netSavings, blueprintData.region || region, currency)}</b>
                    <b>  ROI</b> — <b>{blueprintData.summary.roi?.toFixed(0)}%</b> (net ÷ SpringUp spend).Payback in  <b>month {Math.max(1, Math.round(blueprintData.summary.paybackMonths || 1))}.</b></li>
                </ul>
                <div className="why">Cost is capacity-matched: you avoid the fully-loaded local build-cost while getting equivalent output via the BOT model.</div>

                {/* Charts side-by-side */}
                <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
                  <div className="card chart-card" style={{ flex: '1 1 320px', minWidth: 280 }}>
                    <h4 className="chart-title">CUMULATIVE NET SAVINGS </h4>
                    {chartData.length ? (
                      <AreaChart
                        data={chartData}
                        dataKey="cumulative"
                        color="#1e8e4e"
                        fillOpacity={0.22}
                      />
                    ) : (
                      <div style={{ padding: '18px 0 8px', color: '#4b5563', fontSize: 14, lineHeight: 1.5 }}>
                        The graph updates when the ROI horizon is increased.
                      </div>
                    )}
                  </div>

                  <div className="card chart-card" style={{ flex: '1 1 320px', minWidth: 280 }}>
                    <div className="chart-header-row">
                      <h4 className="chart-title">MONTHLY COST — LOCAL VS. SPRINGUP </h4>
                      <div className="chart-legend">
                        <span className="legend-item">
                          <span className="legend-swatch local" /> Local (in-house)
                        </span>
                        <span className="legend-item">
                          <span className="legend-swatch springup" /> SpringUp (BOT)
                        </span>
                      </div>
                    </div>
                    {chartData.length ? (
                      <DualLineChart data={chartData} />
                    ) : (
                      <div style={{ padding: '18px 0 8px', color: '#4b5563', fontSize: 14, lineHeight: 1.5 }}>
                        The graph updates when the ROI horizon is increased.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>10 · Resource availability in the BOT location</span>
                <span className="tag">Pune / India</span>
              </div>
              <div className="bp-sec-body"><b>Huge market, highly skilled resources immediately available.</b> India adds ~1.5 million engineering graduates and over a million new developers every year; Pune alone has 1M+ IT professionals across product, services and GCCs. That depth means top-5%, English-fluent, internationally-experienced engineers can be sourced and onboarded <b>immediately</b> — enabling fast ramp-up and on-demand scaling up or down without the hiring bottlenecks and risk of the local market.</div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>11 · Contract &amp; governance</span>
                <span className="tag">German entity · EU law</span>
              </div>
              <div className="bp-sec-body"><b>Contracting.</b> The engagement is <b>always contracted with the German legal entity, SpringUp Labs GmbH, under European governing law</b> — a European counterparty, EU/GDPR-aligned data protection and familiar legal recourse, while delivery happens from the India Engineering Center.
                <div ><b>Governance.</b> Implementation, ramp-up and operations are governed on the SpringUp side by <b>German C-level executives who have built and run offshore engineering organisations many times — Markus Doetsch (CTO) and Thomas Lang</b> — ensuring European standards, cultural alignment, and end-to-end accountability from set-up through transfer.</div></div>
            </div>

            <div className="bp-sec">
              <div className="bp-sec-head">
                <span>12 · Risks &amp; assumptions</span>
                <span className="tag">Guardrails</span>
              </div>
              <div className="bp-sec-body"><ul><li>Assumes client availability for interviews &amp; KT during early phases; delays extend the ramp and the ROI updates.</li>
                <li>Compliance (GDPR, ISO 27001) handled via secured VPN/P2P access, policy-controlled laptops and a dedicated secured ODC at SpringUp premises.</li>
                <li>Local benchmarks are researched DE/CH suggestions (fully-loaded); replace with your real costs. SpringUp rates are cost-plus placeholders until quoted.</li></ul></div>
            </div>

            <div className="nav-row">
              <button type="button" className="btn ghost" onClick={() => handleStepClick('roi')}>
                ← Back to ROI
              </button>
              <div className="nav-actions">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={handleExport}
                  disabled={pdfLoading}
                  aria-busy={pdfLoading}
                >
                  {/* Full-page loader for PDF download */}
                  {pdfLoading && (
                    <div
                      style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(255, 255, 255, 0.78)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        backdropFilter: 'blur(3px)',
                      }}
                    >
                      <div
                        className="spinner"
                        style={{ width: 42, height: 42, marginBottom: 18 }}
                      />
                      <div style={{ fontSize: 17, fontWeight: 600, color: '#1e293b' }}>
                        Preparing your PDF…
                      </div>
                      <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
                        This may take a few seconds
                      </div>
                    </div>
                  )}{pdfLoading ? 'Preparing your PDF...' : (
                    <>
                      <FiDownload style={{ marginRight: 8 }} />
                      Download proposal
                    </>
                  )}
                </button>
                {/* <button type="button" className="btn teal" onClick={handleSubmitProposal}>
                  Save proposal
                </button> */}
              </div>
            </div>
            {saveMessage && (
              <div className={`save-banner ${saveStatus === 'error' ? 'error' : saveStatus === 'success' ? 'success' : ''}`}>
                {saveMessage}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="landing-grid">
          {/* ───── LEFT COLUMN ───── */}
          <div className="landing-copy">
            <div className="card scenario-card">
              <div className="card-top-actions">
                <h3>1 · Scenario</h3>

              </div>

              <div className="scenario-tabs">
                {Object.entries(SCENARIO_DESCRIPTIONS_ROI).map(([key]) => (
                  <button
                    key={key}
                    type="button"
                    className={`scenario-tab ${scenario === key ? 'active' : ''}`}
                    onClick={() => setScenario(key)}
                  >
                    <span className="tab-key">{key}</span>
                    <span className="tab-label">
                      {key === 'A' ? 'New dept' : key === 'B' ? 'Add-on' : 'Replace'}
                    </span>
                  </button>
                ))}
              </div>

              <p className="scenario-desc">
                {SCENARIO_DESCRIPTIONS_ROI[scenario] ||
                  'New department. Ramp-up 3–9 months by team size. Start with senior experts who hold the knowledge, build and manage the team around them.'}
              </p>

              {/* ===== Common Field ===== */}
              <div className="form-group">
                <label>ROI horizon</label>
                <div className="range-row">
                  <input
                    type="range"
                    min="12"
                    max="60"
                    step="1"
                    value={horizonMonths}
                    onChange={(e) => setHorizonMonths(Number(e.target.value))}
                  />
                  <span className="range-value">{horizonMonths} mo</span>
                </div>
              </div>

              {/* ===== A · New dept ===== */}
              {(scenario === 'A' || scenario === 'C') && (
                <div className="form-group">
                  <label>Senior-core starting staffing (ramp)</label>
                  <div className="range-row">
                    <input
                      type="range"
                      min="20"
                      max="90"
                      step="1"
                      value={rampPercent}
                      onChange={(e) => setRampPercent(Number(e.target.value))}
                    />
                    <span className="range-value">{rampPercent}%</span>
                  </div>
                </div>
              )}

              {/* ===== B · Add-on ===== */}
              {scenario === 'B' && (
                <div className="form-group">
                  <label>Implementation & search (B)</label>
                  <div className="range-row">
                    <input
                      type="range"
                      min="2"
                      max="12"
                      step="1"
                      value={implementationWeeks}
                      onChange={(e) => setImplementationWeeks(Number(e.target.value))}
                    />
                    <span className="range-value">{implementationWeeks} wk</span>
                  </div>
                </div>
              )}

              {/* ===== C · Replace ===== */}
              {scenario === 'C' && (
                <>
                  <div className="form-group">
                    <label>Handover uplift on ramp (C)</label>
                    <div className="range-row">
                      <input
                        type="range"
                        min="10"
                        max="80"
                        step="1"
                        value={handoverUplift}
                        onChange={(e) => setHandoverUplift(Number(e.target.value))}
                      />
                      <span className="range-value">{handoverUplift}%</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Legacy ramp-down model (C)</label>
                    <select
                      value={legacyRampDownModel}
                      onChange={(e) => setLegacyRampDownModel(e.target.value)}
                    >
                      <option value="A">A · After BOT go-live (conservative)</option>
                      <option value="B">B · In parallel with BOT build (realistic)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Legacy ramp-down duration</label>
                    <div className="range-row">
                      <input
                        type="range"
                        min="1"
                        max="12"
                        step="1"
                        value={legacyRampDownDuration}
                        onChange={(e) => setLegacyRampDownDuration(Number(e.target.value))}
                      />
                      <span className="range-value">{legacyRampDownDuration} mo</span>
                    </div>
                  </div>

                  {legacyRampDownModel === 'A' && (
                    <p className="hint">
                      Conservative. The existing team runs at 100% until the BOT team is fully live,
                      then winds down over {legacyRampDownDuration} mo. In reality you would already
                      ramp it down in parallel — see model B.
                    </p>
                  )}
                </>
              )}

              {/* ===== Common Fields ===== */}
              <div className="form-group">
                <label>SpringUp one-time setup fee (€)</label>
                <div className="input-with-btn">
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={setupFee}
                    onChange={(e) => setSetupFee(Number(e.target.value))}
                  />
                  <button type="button" className="btn-quote">
                    SpringUp quote
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Local benchmark region</label>
                <select value={region} onChange={(e) => setRegion(e.target.value)}>
                  <option value="DE">Germany (DE)</option>
                  <option value="CH">Switzerland (CH)</option>
                  <option value="US">United States (US)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Employer overhead on gross salary</label>
                <div className="range-row">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    value={overhead}
                    onChange={(e) => setOverhead(Number(e.target.value))}
                  />
                  <span className="range-value">{overhead}%</span>
                </div>
              </div>

              <p className="hint">
                Local cost = gross salary + social fees, insurances, office &amp; overhead
                (typically ~25% on top in DE/CH). Benchmarks are researched suggestions —
                overwrite with your real costs if known.
              </p>
            </div>

            <div className="card derived-card">
              <h4>DERIVED</h4>
              <ul className="derived-list">
                <li>
                  <span>Team size:</span>
                  <strong>
                    {Number(summary.teamSize ?? 15).toFixed(2)} FTE
                  </strong>
                </li>
                <li>
                  <span>Ramp base (size):</span>
                  <strong>{summary.rampBase ?? '4.9'} mo</strong>
                </li>
                <li>
                  <span>Effective ramp / setup:</span>
                  <strong>{summary.effectiveRamp ?? '4.9'} mo</strong>
                </li>
                <li>
                  <span>Total local cost (horizon):</span>
                  <strong>{formatCurrency(summary.totalLocalCost ?? 4338415, region, currency)}</strong>
                </li>
                <li>
                  <span>Total SpringUp cost (incl. fees):</span>
                  <strong>{formatCurrency(summary.totalSpringUpCost ?? 1785917, region, currency)}</strong>
                </li>
              </ul>
            </div>
            <div className="card chart-card">
              <h4 className="chart-title">CUMULATIVE NET SAVINGS </h4>
              {chartData.length ? (
                <AreaChart
                  data={chartData}
                  dataKey="cumulative"
                  color="#1e8e4e"
                  fillOpacity={0.22}
                />
              ) : (
                <div style={{ padding: '18px 0 8px', color: '#4b5563', fontSize: 14, lineHeight: 1.5 }}>
                  The graph updates when the ROI horizon is increased.
                </div>
              )}
            </div>

            <div className="card chart-card">
              <div className="chart-header-row">
                <h4 className="chart-title">MONTHLY COST — LOCAL VS. SPRINGUP </h4>
                <div className="chart-legend">
                  <span className="legend-item">
                    <span className="legend-swatch local" /> Local (in-house)
                  </span>
                  <span className="legend-item">
                    <span className="legend-swatch springup" /> SpringUp (BOT)
                  </span>
                </div>
              </div>
              {chartData.length ? (
                <DualLineChart data={chartData} />
              ) : (
                <div style={{ padding: '18px 0 8px', color: '#4b5563', fontSize: 14, lineHeight: 1.5 }}>
                  The graph updates when the ROI horizon is increased.
                </div>
              )}
            </div>
          </div>

          {/* ───── RIGHT COLUMN ───── */}
          <div className="landing-panel">
            <div className="metrics-row">
              <div className="metric-card">
                <div className="metric-label">LOCAL / MONTH</div>
                <div className="metric-value">{formatCurrency(summary.localTotal, region, currency)}</div>
                <div className="metric-sub">full operation</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">SPRINGUP / MONTH</div>
                <div className="metric-value">{formatCurrency(summary.springUpBase ?? roleCosts.reduce((s, r) => s + r.springUpMonthly, 0), region, currency)}</div>
                <div className="metric-sub">full operation</div>
              </div>
              <div className="metric-card good">
                <div className="metric-label">SAVING / MONTH</div>
                <div className="metric-value">{formatCurrency((summary.localTotal ?? 0) - (summary.springUpBase ?? roleCosts.reduce((s, r) => s + r.springUpMonthly, 0)), region, currency)}
                </div>

                <div className="metric-sub">
                  {summary.savingsPct?.toFixed(1) ?? '59.4'}% vs local
                </div>
              </div>
            </div>

            <div className="metrics-row secondary">
              <div className="metric-card">
                <div className="metric-label">NET SAVINGS (HORIZON)</div>
                <div className="metric-value green">
                  {formatCurrency(summary.netSavings, region, currency)}
                </div>
                <div className="metric-sub">vs. building locally</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">ROI</div>
                <div className="metric-value green">
                  {summary.roi?.toFixed(0) ?? 143}%
                </div>
                <div className="metric-sub">net ÷ SpringUp cost</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">PAYBACK</div>
                <div className="metric-value">
                  M{Math.max(1, Math.round(summary.paybackMonths ?? 1))}
                </div>
                <div className="metric-sub">months to break even</div>
              </div>
            </div>

            {/* 2 · ROLES TABLE */}
            <div className="card roles-card">
              <h3>2 · Roles, seniority &amp; cost</h3>
              {validationMessage && activeView === 'roi' && (
                <div className="error-banner">{validationMessage}</div>
              )}
              {validationErrors.roles && <div className="field-error-message" style={{ marginBottom: '12px' }}>{validationErrors.roles}</div>}

              <div className="table-wrap">
                <table className="roles-table">
                  <thead>
                    <tr>
                      <th>ROLE</th>
                      <th>SENIORITY</th>
                      <th>FTE</th>
                      <th>LOCAL {currency}/FTE MO</th>
                      <th>SPRINGUP {currency}/FTE MO</th>
                      <th>LOCAL {currency}/MO</th>
                      <th>SPRINGUP {currency}/MO</th>
                      <th>SAVE {currency}/MO</th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {roleCosts.map((role, index) => (
                      <tr key={`${role.name}-${index}`}>
                        {/* ROLE (roleType select) */}
                        <td>
                          <div className="table-field">
                            <select
                              className={validationErrors[`roleType_${index}`] ? 'field-error' : ''}
                              value={roles[index]?.roleType ?? role.roleType}
                              onChange={(e) => updateRole(index, 'roleType', e.target.value)}
                            >
                              <option value="">Select role type</option>
                              {(roleTypes.length ? roleTypes : ROLE_TYPES || [
                                'Architecture / Eng Mgmt',
                                'Backend',
                                'Frontend',
                                'Database',
                                'DevOps / SRE',
                                'QA / Test',
                                'Scrum',
                                'Business',
                              ]).map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                            {validationErrors[`roleType_${index}`] && (
                              <div className="field-error-message">
                                {validationErrors[`roleType_${index}`]}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* SENIORITY */}
                        <td>
                          <div className="table-field">
                            <select
                              className={validationErrors[`roleSeniority_${index}`] ? 'field-error' : ''}
                              value={(() => {
                                const currentRoleType = roles[index]?.roleType ?? role.roleType;
                                const currentSeniority = roles[index]?.seniority ?? role.seniority;

                                let options = ['Mid', 'Senior', 'Lead'];
                                if (currentRoleType === 'Management') {
                                  options = ['CTO', 'PM', 'Account Manager'];
                                } else if (currentRoleType === 'Technical Architect') {
                                  options = ['Solution Architect', 'Enterprise Architect'];
                                } else if (currentRoleType === '24x7 Support') {
                                  options = ['DevOps/Cloud Support', 'Application Support'];
                                }

                                return options.includes(currentSeniority) ? currentSeniority : options[0];
                              })()}
                              onChange={(e) => updateRole(index, 'seniority', e.target.value)}
                            >
                              {(() => {
                                const currentRoleType = roles[index]?.roleType ?? role.roleType;
                                let options = ['Mid', 'Senior', 'Lead'];

                                if (currentRoleType === 'Management') {
                                  options = ['CTO', 'PM', 'Account Manager'];
                                } else if (currentRoleType === 'Technical Architect') {
                                  options = ['Solution Architect', 'Enterprise Architect'];
                                } else if (currentRoleType === '24x7 Support') {
                                  options = ['DevOps/Cloud Support', 'Application Support'];
                                }

                                return options.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ));
                              })()}
                            </select>
                            {validationErrors[`roleSeniority_${index}`] && (
                              <div className="field-error-message">
                                {validationErrors[`roleSeniority_${index}`]}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* FTE */}
                        <td>
                          <div className="table-field">
                            <input
                              type="number"
                              className={`cell-input fte ${validationErrors[`roleFte_${index}`] ? 'field-error' : ''}`}
                              min="0.5"
                              step="0.5"
                              value={roles[index]?.fte ?? role.fte}
                              onChange={(e) => updateRole(index, 'fte', Number(e.target.value))}
                            />
                            {validationErrors[`roleFte_${index}`] && (
                              <div className="field-error-message">
                                {validationErrors[`roleFte_${index}`]}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* LOCAL €/FTE MO */}
                        <td>
                          <input
                            type="number"
                            className="cell-input num"
                            min="0"
                            step="50"
                            value={
                              roles[index]?.localPerFte !== undefined && roles[index]?.localPerFte !== ''
                                ? roles[index].localPerFte
                                : (role.localPerFte !== undefined && role.localPerFte !== ''
                                  ? role.localPerFte
                                  : role.localRate ?? '')
                            }
                            onChange={(e) => updateRole(index, 'localPerFte', Number(e.target.value))}
                          />
                        </td>

                        {/* SPRINGUP €/FTE MO */}
                        <td>
                          <input
                            type="number"
                            className="cell-input num springup-rate"
                            min="0"
                            step="50"
                            value={
                              roles[index]?.springUpPerFte !== undefined && roles[index]?.springUpPerFte !== ''
                                ? roles[index].springUpPerFte
                                : (role.springUpPerFte !== undefined && role.springUpPerFte !== ''
                                  ? role.springUpPerFte
                                  : role.springUpRate ?? '')
                            }
                            onChange={(e) => updateRole(index, 'springUpPerFte', Number(e.target.value))}
                          />
                        </td>

                        {/* LOCAL €/MO (read-only) */}
                        <td className="num">
                          {formatCurrency(role.localMonthly, region, currency)}
                        </td>

                        {/* SPRINGUP €/MO (read-only) */}
                        <td className="num">
                          {formatCurrency(role.springUpMonthly, region, currency)}
                        </td>

                        {/* SAVE €/MO */}
                        <td className="num save">
                          {formatCurrency(role.localMonthly - role.springUpMonthly, region, currency)}
                        </td>

                        {/* Remove button */}
                        <td>
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => removeRole(index)}
                            aria-label="Remove role"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr>
                      <td colSpan={2}>
                        <strong>TOTAL (full operation)</strong>
                      </td>
                      <td>
                        <strong style={{ display: 'flex', justifyContent: 'center' }}>
                          {(summary.teamSize ?? roleCosts.reduce((s, r) => s + Number(r.fte || 0), 0)).toFixed(2)}
                        </strong>
                      </td>
                      <td></td>
                      <td></td>
                      <td className="num">
                        <strong>{formatCurrency(summary.localTotal, region, currency)}</strong>
                      </td>
                      <td className="num">
                        <strong>
                          {formatCurrency(
                            summary.springUpBase ?? roleCosts.reduce((s, r) => s + r.springUpMonthly, 0),
                            region,
                            currency
                          )}
                        </strong>
                      </td>
                      <td className="num save">
                        <strong>
                          {formatCurrency(
                            (summary.localTotal ?? 0) -
                            (summary.springUpBase ?? roleCosts.reduce((s, r) => s + r.springUpMonthly, 0)),
                            region,
                            currency
                          )}
                        </strong>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <button type="button" className="btn-add-role" onClick={addRole}>
                + Add role
              </button>

              <div className="table-notes">
                <p>
                  <strong>Local {currency}/mo</strong> auto-fills from researched DE/CHF/US salary benchmarks
                  (role type × seniority) + overhead — <em>overwrite with your real costs</em>.
                </p>
                <p>
                  <strong>SpringUp {currency}/mo</strong> auto-fills from the SpringUp rate card. Changing
                  role type or seniority re-applies both; manual edits override.
                </p>
                <p className="rate-card">
                  Local benchmark — Germany, loaded (gross × 1.25), {currency}/mo: Frontend Sr 8.300 · Backend
                  Sr 9.150 · DevOps Sr 10.000 · QA Sr 8.050 · Data Sr 10.400 · Architecture Sr 12.450
                </p>
                <p className="rate-card">
                  SpringUp card {currency}/mo:  Mid 2800 · Senior 3800 · Lead 4800 · CTO 6000
                </p>
              </div>
            </div>

            {/* ───── CHARTS ───── */}
            {/* Role profiles panel (fetched from backend) */}
            <RoleProfilesPanel selectedRoles={roles} />

            <button
              style={{
                marginTop: '20px',
                display: 'block',
                marginLeft: 'auto'
              }}
              type="button"
              className="btn primary"
              onClick={handleContinue}
            >
              Generate blueprint →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;